/**
 * Sending the composer's letter: the confirmation it opens, and what happens
 * once the operator says yes - sent now, or scheduled.
 *
 * Moved out of `EmailSection` unchanged, which had grown past the size this repo
 * lets a file reach without being split (AGENTS.md section 4). The section keeps
 * the state the flow reads; this hook keeps the flow itself and the dialog.
 */
import { useState, type MutableRefObject } from "react";
import { useT } from "../../../i18n/I18nProvider";
import { replyThread, sendMail, type MailStats, type MailThread } from "../../../lib/mail";
import { markBrainEmailSent } from "../../../lib/siteBrainEmail";
import { markEstimateSent } from "../../../lib/sales";
import { useToast } from "../../ui";
import { SendConfirmModal, type SendPreview } from "../../mail/SendConfirmModal";
import type { ComposerDraft, ComposerSendExtra, ComposerValues } from "./Composer";
import { draftRequestError } from "./drafts";
import { replyRecipients } from "./mailbox";

export function useSendFlow({
  draft,
  thread,
  stats,
  closeComposer,
  loadThread,
  loadList,
  refresh,
  refreshStats,
  scheduleLetter,
  brainDomain,
}: {
  draft: ComposerDraft | null;
  thread: MailThread | null;
  stats: MailStats | null;
  closeComposer: () => void;
  loadThread: (id: string) => void;
  loadList: () => Promise<unknown>;
  refresh: () => void;
  refreshStats: () => void;
  scheduleLetter: (extra: ComposerSendExtra) => Promise<void>;
  /** The company whose brain letter is open, so a successful send can be stamped. */
  brainDomain: MutableRefObject<string | null>;
}) {
  const t = useT();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  /** A composed message waiting on the confirmation dialog. */
  const [pending, setPending] = useState<{ values: ComposerValues; preview: SendPreview; extra: ComposerSendExtra } | null>(null);

  /**
   * Pressing send opens the confirmation instead of sending. The draft stays
   * mounted behind it, so cancelling returns to the composer with the text
   * intact rather than to an empty screen.
   */
  function requestSend(values: ComposerValues, extra: ComposerSendExtra) {
    if (!draft) return;
    // A reply's recipients live on the thread, not in the form. Resolving them
    // here is what lets the dialog name them, and the same list is then sent
    // explicitly so the preview and the send cannot disagree.
    //
    // Resolved off the OPEN thread only when it is the one being replied to. A
    // reply can now be started from a list row, and reading the recipients off
    // whatever happens to be open would address it to the wrong company.
    const replyTarget = draft.threadId && thread?.id === draft.threadId ? thread : null;
    const to = draft.mode === "reply" && draft.threadId ? replyRecipients(replyTarget) : values.to;
    setPending({
      values,
      extra,
      preview: {
        scheduledAt: extra.scheduleAt,
        // The From line the composer picked, as the recipient will read it.
        // The same address is sent below, so the dialog and the letter agree.
        from: values.fromLine || stats?.sendingAddress || stats?.inboundAddress || undefined,
        to,
        cc: values.cc,
        bcc: values.bcc,
        subject: values.subject,
        // The server wraps this in `white-space:pre-wrap` and appends the
        // signature the composer chose; the note says which one rather than the
        // preview trying to render it.
        bodyText: values.body,
        // The last place a wrong file can still be caught. An attachment is the
        // part of a message that cannot be explained away in a follow-up.
        attachments: values.attachments.map((a) => a.name),
        note:
          values.signatureId === null
            ? t("sendConfirm.signatureNone")
            : t("sendConfirm.signatureNote"),
      },
    });
  }

  async function confirmSend() {
    if (!draft || !pending) return;
    const { values, preview, extra } = pending;
    setSending(true);
    try {
      if (extra.scheduleAt) {
        await scheduleLetter(extra);
        setPending(null);
        closeComposer();
        return;
      }
      // The draft behind the window, saved to its last word so the server can
      // delete exactly it once the letter is out. A draft that cannot be saved
      // must not hold up the send: the letter matters more than its copy.
      const draftId = (await extra.draft.flush().catch(() => null)) ?? undefined;
      // The composer has already split and de-duplicated the addresses, so
      // nothing here has to parse a string. `cc` is omitted rather than sent
      // empty: the API treats an absent key and an empty list the same, and an
      // empty array in the payload reads like a deliberate "no copies".
      const cc = values.cc.length ? values.cc : undefined;
      const bcc = values.bcc.length ? values.bcc : undefined;
      // Same treatment as `cc`: absent rather than empty, so a message with no
      // files does not carry a key that reads as a deliberate empty list.
      const attachmentIds = values.attachments.length ? values.attachments.map((a) => a.id) : undefined;
      if (draft.mode === "reply" && draft.threadId) {
        await replyThread(draft.threadId, {
          body: values.body, bodyHtml: values.bodyHtml, cc, bcc, to: preview.to, attachmentIds,
          signatureId: values.signatureId, fromAddress: values.fromAddress, draftId,
        });
        loadThread(draft.threadId);
      } else {
        await sendMail({
          to: values.to, cc, bcc, subject: values.subject, body: values.body,
          bodyHtml: values.bodyHtml, attachmentIds,
          signatureId: values.signatureId, fromAddress: values.fromAddress, draftId,
        });
        await loadList();
        // The letter came from the development brain, so the brain gets to
        // record that it went out. Without this stamp the whole feature is a
        // text generator: nobody can answer "which of the three asks got
        // replies", and the variants stay three opinions instead of a measured
        // choice.
        //
        // Deliberately swallowed and deliberately after the send: a failed
        // stamp must never read to an operator as a failed send, because the
        // letter is already gone.
        if (brainDomain.current) {
          void markBrainEmailSent(brainDomain.current).catch(() => {});
          brainDomain.current = null;
        }
        // The letter carried a quote, so the quote is now sent. Read off the
        // draft rather than a ref: the link belongs to this one letter, and a
        // later letter from the same screen must not stamp it. Swallowed for
        // the same reason as the brain stamp - the letter is already gone.
        if (draft.salesDoc) void markEstimateSent(draft.salesDoc.id).catch(() => {});
      }
      // Before the window closes: a closing window saves what it has, and the
      // letter it would save has just been sent.
      extra.draft.stop();
      setPending(null);
      closeComposer();
      refresh();
      refreshStats();
      toast(t("compose.sent"));
    } catch (err) {
      toast(draftRequestError(t, err));
    } finally {
      setSending(false);
    }
  }

  // Above the composer rather than instead of it: cancelling has to return to
  // the text that was typed, not to an empty screen.
  const confirm = pending ? (
    <SendConfirmModal
      preview={pending.preview}
      busy={sending}
      onCancel={() => setPending(null)}
      onConfirm={confirmSend}
    />
  ) : null;

  return { sending, requestSend, confirm };
}
